import { createAsyncThunk } from '@reduxjs/toolkit';

import sendRequest from '../../../service/sendRequest';
import consumerApi, { AUTH_SHAPE } from '../../../service/consumerApi';
import { enqueueSnackbar, somethingWrongNotification } from '../../notifier/notifierActions';
import { clearConsumerSession, setConsumerSession } from '../consumerReducer';
import { pickCredentials } from '../consumerSession';
import { ID8_TABS, PROJECT_TYPES, PROMPT_TYPE } from '../../../constants';
import { downloadDocument } from '../../../utils/downloadDocument';
import { changeLocalMessage, likeLocalMessage, setEditableMessage, setIsDirty } from '../../id8/id8Reducer';
import { openProject } from '../../global/globalReducer';
import generateFileName from '../../../utils/generateFileName';
import downloadImage from '../../../utils/downloadImage';
import { getConsumerId8Image } from '../../../utils/id8ImagesRequests';
import { addPromptToImages } from '../../id8/id8Actions';

const GET_ID8_DATA = 'consumer/id8/GET_ID8_DATA';

const GET_IDEAS_MESSAGES_CONSUMER = 'consumer/id8/GET_IDEAS_MESSAGES_CONSUMER';
const GET_MESSAGES_BY_TAB_CONSUMER = 'consumer/id8/GET_MESSAGES_BY_TAB_CONSUMER';

const RESET_SESSION_CONSUMER = 'consumer/id8/RESET_SESSION_CONSUMER';
const REPLY_ID8_CONSUMER = 'consumer/id8/REPLY_ID8_CONSUMER';
const GENERATE_MESSAGES_CONSUMER = 'consumer/id8/GENERATE_MESSAGES_CONSUMER';

const LIKE_MESSAGE_CONSUMER = 'consumer/id8/LIKE_MESSAGE_CONSUMER';
const DELETE_MESSAGE_CONSUMER = 'consumer/id8/DELETE_MESSAGE_CONSUMER';
const SELECT_MESSAGE_CONSUMER = 'consumer/id8/SELECT_MESSAGE_CONSUMER';
const GET_MESSAGES_LOGS_CONSUMER = 'consumer/id8/GET_MESSAGES_LOGS_CONSUMER';
const SAVE_MESSAGE_CONSUMER = 'consumer/id8/SAVE_MESSAGE_CONSUMER';

const CREATE_MESSAGE_CONSUMER = 'consumer/id8/CREATE_MESSAGE_CONSUMER';
const EXPORT_MESSAGES_CONSUMER = 'consumer/id8/EXPORT_MESSAGES_CONSUMER';

const GET_IMAGES = 'consumer/id8/GET_IMAGES';
const DOWNLOAD_IMAGE = 'consumer/id8/DOWNLOAD_IMAGE';
const LIKE_IMAGE = 'consumer/id8/LIKE_IMAGE';
const RUN_AGAIN = 'consumer/id8/RUN_AGAIN';
const MODIFY_CONCEPT = 'consumer/id8/MODIFY_CONCEPT';
const CUSTOM_IMAGE = 'consumer/id8/CUSTOM_IMAGE';
const REVISE_IMAGE = 'consumer/id8/REVISE_IMAGE';
const GET_PROMPT_BY_ID = 'consumer/id8/GET_PROMPT_BY_ID';

const GET_AD_IMAGES = 'consumer/id8/GET_AD_IMAGES';
export const RECREATE_AD = 'consumer/id8/RECREATE_AD';
export const RUN_AGAIN_AD = 'consumer/id8/RUN_AGAIN_AD';
export const MODIFY_CONCEPT_AD = 'consumer/id8/MODIFY_CONCEPT_AD';

export const CONSUMER_IMAGE_GENERATION_ACTIONS = {
  RUN_AGAIN,
  MODIFY_CONCEPT,
  CUSTOM_IMAGE,
  REVISE_IMAGE,
};

export const getConsumerId8Data = createAsyncThunk(
  GET_ID8_DATA,
  async ({ projectName, projectPassword }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8`, null, {
          authShape: AUTH_SHAPE.FLAT,
          consumerCredentials: { projectName, projectPassword },
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    dispatch(
      setConsumerSession({
        projectType: PROJECT_TYPES.ID8,
        projectName,
        projectPassword,
      }),
    );

    dispatch(openProject(projectName));

    return response.data;
  },
);

//MESSAGES LOADING
const loadConsumerMessages = async (getState, options = {}) => {
  const { sort, searchQuery } = getState().id8.tableSettings;

  const requestBody = {
    ...(searchQuery ? { word: searchQuery } : {}),
    ...options,
  };

  const response = await sendRequest((cancelToken) =>
    consumerApi.post(`/api/consumer/id8/messages/search?sort=${sort}`, requestBody, {
      authShape: AUTH_SHAPE.PROJECT_REQUEST,
      cancelToken,
    }),
  );

  return response.data;
};

export const getIdeasMessagesConsumer = createAsyncThunk(GET_IDEAS_MESSAGES_CONSUMER, async (_, { getState }) =>
  loadConsumerMessages(getState, {}),
);

export const getMessagesByTab = createAsyncThunk(
  GET_MESSAGES_BY_TAB_CONSUMER,
  async (_, { getState, dispatch, rejectWithValue }) => {
    const id8Tab = getState().id8.id8Tab;

    if (!pickCredentials(getState().consumer.session)) {
      return rejectWithValue(null);
    }

    if (id8Tab === ID8_TABS.IDEAS) {
      dispatch(getIdeasMessagesConsumer());
    }
  },
);

//FORM ACTIONS
export const resetSessionConsumer = createAsyncThunk(
  RESET_SESSION_CONSUMER,
  async (_, { rejectWithValue, dispatch }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/reset`, null, {
          authShape: AUTH_SHAPE.FLAT,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    return response.data;
  },
);

export const replyId8Consumer = createAsyncThunk(
  REPLY_ID8_CONSUMER,
  async ({ reply }, { rejectWithValue, dispatch }) => {
    const requestBody = reply;

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/reply`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    dispatch(getMessagesByTab());

    return response.data;
  },
);

export const generateMessagesConsumer = createAsyncThunk(
  GENERATE_MESSAGES_CONSUMER,
  async ({ values }, { rejectWithValue, dispatch }) => {
    const { byPass, focusOn, url, fileText } = values;

    if (!url) {
      return rejectWithValue(null);
    }

    const requestBody = {
      byPass,
      focusOn,
      url,
      fileText,
    };

    const response = await sendRequest(
      (cancelToken) =>
        consumerApi.post(`/api/consumer/id8/generate/messages`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
          cancelToken,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    dispatch(getMessagesByTab());

    return response.data;
  },
);

//MESSAGE ACTIONS
export const likeMessageConsumer = createAsyncThunk(
  LIKE_MESSAGE_CONSUMER,
  async ({ id, reaction, createType }, { dispatch, rejectWithValue }) => {
    dispatch(likeLocalMessage({ id, reaction, createType }));

    const requestBody = {
      id,
      ...(reaction ? { reaction } : {}),
    };

    await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/messages/reaction`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );
  },
);

export const deleteMessageConsumer = createAsyncThunk(
  DELETE_MESSAGE_CONSUMER,
  async ({ id }, { dispatch, rejectWithValue }) => {
    await sendRequest(
      () =>
        consumerApi.delete(`/api/consumer/id8/messages`, {
          data: id,
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => {
          dispatch(
            enqueueSnackbar({
              message: 'couldntDeleteMessage',
              options: {
                variant: 'error',
              },
            }),
          );
          return rejectWithValue(e);
        },
      },
    );

    dispatch(
      enqueueSnackbar({
        message: 'messageHasBeenDeleted',
        options: {
          variant: 'success',
        },
      }),
    );

    return id;
  },
);

export const selectMessageConsumer = createAsyncThunk(
  SELECT_MESSAGE_CONSUMER,
  async ({ id, selected }, { rejectWithValue, dispatch }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/messages/${selected ? 'unselect' : 'select'}`, id, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    if (!selected) {
      const requestBody = id;

      await sendRequest(
        () =>
          consumerApi.post(`/api/consumer/id8/content/idea`, requestBody, {
            authShape: AUTH_SHAPE.PROJECT_REQUEST,
          }),
        {
          onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
        },
      );
    }

    return { id, selected: !selected, createType: response.data.createType };
  },
);

export const getMessageLogsConsumer = createAsyncThunk(
  GET_MESSAGES_LOGS_CONSUMER,
  async ({ messageId }, { rejectWithValue, dispatch }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/messages/history`, messageId, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    return response.data;
  },
);

export const saveMessageChangesConsumer = createAsyncThunk(
  SAVE_MESSAGE_CONSUMER,
  async (_, { dispatch, getState, rejectWithValue }) => {
    const {
      editableMessage: { id, message },
    } = getState().id8;

    dispatch(changeLocalMessage({ id, message }));
    dispatch(setEditableMessage({ id: null, message: '' }));
    dispatch(setIsDirty(false));

    const requestBody = { message };

    await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/messages/edit-message/${id}`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );
  },
);

//FILTER ACTIONS
export const createMessageConsumer = createAsyncThunk(
  CREATE_MESSAGE_CONSUMER,
  async ({ messages }, { dispatch, rejectWithValue }) => {
    const messagesToCreate = messages.map(({ message }) => message);

    if (!messagesToCreate.length) {
      return rejectWithValue(null);
    }

    const requestBody = messagesToCreate;

    await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/messages/create`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        successStatusCode: 201,
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    dispatch(
      enqueueSnackbar({
        message: 'messageWasCreatedSuccessfully',
        options: {
          variant: 'success',
        },
      }),
    );

    dispatch(getMessagesByTab());
  },
);

export const exportMessagesConsumer = createAsyncThunk(
  EXPORT_MESSAGES_CONSUMER,
  async (_, { rejectWithValue, getState, dispatch }) => {
    const messages = getState().id8.messagesContent.map(({ id }) => id);
    const projectName = getState().id8.projectName;

    if (!messages.length) {
      dispatch(
        enqueueSnackbar({
          message: 'noMessagesAvailable',
          options: {
            variant: 'warning',
            subMessage: 'pleaseGenerateMessagesBefore',
          },
        }),
      );
      return rejectWithValue(null);
    }

    const requestBody = messages;

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/messages/export`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
          responseType: 'blob',
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    const fileName = generateFileName({ name: projectName, ext: 'txt' });

    downloadDocument(fileName, response.data);
  },
);

//IMAGES ACTIONS
const loadConsumerImages = async ({ options = {}, dispatch, rejectWithValue }) => {
  const { searchQuery, page, sort, size, ...additionalOptions } = options;

  const requestBody = {
    ...additionalOptions,
    ...(searchQuery ? { query: searchQuery } : {}),
  };

  const response = await sendRequest(
    () =>
      consumerApi.post(`/api/consumer/id8/content/search?&page=${page}&size=${size}&sort=${sort}`, requestBody, {
        authShape: AUTH_SHAPE.PROJECT_REQUEST,
      }),
    {
      onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
    },
  );

  return response.data;
};

export const getConsumerImages = createAsyncThunk(GET_IMAGES, async (_, { rejectWithValue, dispatch, getState }) => {
  const { searchQuery, page, sort, size } = getState().id8.imagesTableSettings;

  const response = await loadConsumerImages({
    options: { searchQuery, page, sort, size, types: [PROMPT_TYPE.IDEA] },
    dispatch,
    rejectWithValue,
  });

  return { content: response.content, totalElements: response.totalElements };
});

export const getConsumerAdImages = createAsyncThunk(
  GET_AD_IMAGES,
  async (_, { rejectWithValue, dispatch, getState }) => {
    const { searchQuery, page, sort, size } = getState().id8.adImagesTableSettings;

    const response = await loadConsumerImages({
      options: { searchQuery, page, sort, size, types: [PROMPT_TYPE.RECREATE] },
      dispatch,
      rejectWithValue,
    });

    return { content: response.content, totalElements: response.totalElements };
  },
);

export const consumerDownloadId8Image = createAsyncThunk(
  DOWNLOAD_IMAGE,
  async (imageId, { rejectWithValue, dispatch }) => {
    const response = await sendRequest(() => getConsumerId8Image(imageId), {
      onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
    });

    downloadImage(imageId, response.data);
  },
);

export const consumerLikeId8Image = createAsyncThunk(
  LIKE_IMAGE,
  async ({ imageId, newReaction, updatedOn, promptType }, { rejectWithValue, dispatch }) => {
    sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/content/react?imageId=${imageId}&reaction=${newReaction}`, null, {
          authShape: AUTH_SHAPE.FLAT,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    return { imageId, newReaction, updatedOn, promptType };
  },
);

export const consumerRunAgain = createAsyncThunk(RUN_AGAIN, async (_, { rejectWithValue, dispatch, getState }) => {
  const imageId = getState().id8.imagesContent[0].id;

  if (!imageId) {
    return rejectWithValue(null);
  }

  await sendRequest(
    () =>
      consumerApi.post(`/api/consumer/id8/content/re-generate?ideaPromptId=${imageId}`, null, {
        authShape: AUTH_SHAPE.FLAT,
      }),
    {
      onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
    },
  );

  dispatch(getConsumerImages());
});

export const consumerModifyConcept = createAsyncThunk(
  MODIFY_CONCEPT,
  async ({ ideaPromptId, newConcept }, { rejectWithValue, dispatch }) => {
    const requestBody = newConcept;

    await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/content/modify/concept?ideaPromptId=${ideaPromptId}`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    dispatch(getConsumerImages());
  },
);

export const consumerCustomImage = createAsyncThunk(
  CUSTOM_IMAGE,
  async ({ description }, { rejectWithValue, dispatch }) => {
    const requestBody = description;

    await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/content/custom-idea`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    dispatch(getConsumerImages());
  },
);

export const consumerReviseImage = createAsyncThunk(
  REVISE_IMAGE,
  async ({ imageId, description, file }, { rejectWithValue, dispatch }) => {
    const formData = new FormData();

    if (file) {
      formData.append('file', file, file.name);
    }

    await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/content/revise`, formData, {
          authShape: AUTH_SHAPE.QUERY,
          params: { imageId, revision: description },
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );
  },
);

export const consumerGetPromptById = createAsyncThunk(
  GET_PROMPT_BY_ID,
  async ({ bucketId, promptType }, { rejectWithValue, dispatch, getState }) => {
    const requestBody = bucketId;

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/content/find/by/id`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    const isPromptExist =
      getState().id8.imagesContent.find((image) => image.id === response.data.id) ||
      getState().id8.adImagesContent.find((image) => image.id === response.data.id);

    if (!isPromptExist) {
      await dispatch(addPromptToImages({ promptId: bucketId, promptType }));
    }

    return { prompt: response.data, promptType };
  },
);

// AD IMAGES
export const recreateAdConsumer = createAsyncThunk(RECREATE_AD, async ({ image, description }) => {
  const formData = new FormData();
  formData.append('file', image, image.name);

  const response = await sendRequest(() =>
    consumerApi.post(`/api/consumer/id8/content/recreate`, formData, {
      authShape: AUTH_SHAPE.QUERY,
      params: { description },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  );

  return response.data;
});

export const runAgainAdConsumer = createAsyncThunk(RUN_AGAIN_AD, async (_, { rejectWithValue, dispatch, getState }) => {
  const promptId = getState().id8.adImagesContent[0]?.id;

  if (!promptId) {
    return rejectWithValue(null);
  }

  await sendRequest(
    () =>
      consumerApi.post(`/api/consumer/id8/content/re-generate?ideaPromptId=${promptId}`, null, {
        authShape: AUTH_SHAPE.FLAT,
      }),
    {
      onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
    },
  );
});

export const modifyAdConceptConsumer = createAsyncThunk(
  MODIFY_CONCEPT_AD,
  async ({ newConcept }, { rejectWithValue, dispatch, getState }) => {
    const promptId = getState().id8.adImagesContent[0]?.id;

    const requestBody = newConcept;

    if (!promptId) {
      return rejectWithValue(null);
    }

    await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/id8/content/modify/concept?ideaPromptId=${promptId}`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );
  },
);

const defaultErrorHandler = (e, dispatch, rejectWithValue) => {
  if (e.response.status === 409) {
    dispatch(
      enqueueSnackbar({
        message: 'projectInUse',
        options: {
          variant: 'warning',
          subMessage: 'thisProjectIsCurrentlyUsing',
          subMessageParams: {
            username: e.response.data.username ?? '',
          },
        },
      }),
    );
    dispatch(clearConsumerSession());

    return rejectWithValue(e);
  }

  dispatch(somethingWrongNotification());
  return rejectWithValue(e);
};
